import get from 'lodash-es/get'
import Head from 'next/head'
import { withRouter } from 'next/router'
import React, { createRef, Component } from 'react'
import Slider from 'react-slick'
import Badge from '#/components/Badge'
import FAQ from '#/components/FAQ'
import OverlaySpinner from '#/components/OverlaySpinner'
import Page from '#/components/Page'
import { trialDays } from '#/configs'
import { pricing as faq } from '#/consts/faq'
import plans, { id2Plan, features } from '#/consts/plan'
import { ACADEMY_PAGE, FAQ_PAGE } from '#/consts/url'
import { logEvent } from '#/services/analytics'
import { GetCustomer } from '#/store/actions/user'
import cn from '#/utils/classnames'
import intercomApi from '#/utils/intercomApi'
import { Feature, FeaturesHeader } from './components/Feature'
import PeriodPicker, { Period } from './components/PeriodPicker'
import PlanCard from './components/PlanCard'
import PlanInfo from './components/PlanInfo'
import styles from './styles.module.scss'
import { Props, State } from './types'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'

class Pricing extends Component<Props, State> {
  refPlanHeaders: React.RefObject<HTMLDivElement>

  constructor(props: Readonly<Props>) {
    super(props)

    this.state = {
      period: 'monthly',
      isPlanHeaderSticky: false
    }

    this.refPlanHeaders = createRef()
  }

  componentDidMount() {
    window.addEventListener('scroll', this.onScroll)
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.onScroll)
  }

  onScroll = () => {
    if (this.refPlanHeaders.current) {
      const isSticky = this.refPlanHeaders.current.offsetTop > 0
      if (this.state.isPlanHeaderSticky !== isSticky) {
        this.setState({ isPlanHeaderSticky: isSticky })
      }
    }
  }

  onPeriodChange = (interval: Period) => {
    const { period } = this.state
    if (period !== interval) {
      logEvent('select_content', {
        content_type: 'pricing',
        item_id: 'period/change',
        value: interval
      })

      this.setState({ period: interval })
    }
  }

  // onFree = () => {
  //   const { router } = this.props
  //   router.replace({
  //     pathname: router.pathname,
  //     query: { auth: 'signup' }
  //   })
  // }

  onEnterprise = () => {
    intercomApi('show')
    logEvent('generate_lead', { content_type: 'enterprise_interest' })
    intercomApi('trackEvent', 'enterprise_interest')
  }

  showIntercom = () => {
    intercomApi('show')
  }

  get pageTitle() {
    const { user } = this.props
    const isSubscribed = Boolean(get(user.paymentInfo, 'plan'))
    const currentRole = get(user.paymentInfo, 'role')

    let title

    if (currentRole) {
      // when user logged in
      if (isSubscribed) {
        if (currentRole === 'expert') {
          title = 'Cheers, you are a TopHap Expert! 🥂'
        } else if (currentRole === 'pro') {
          title = (
            <>
              <span className={styles['th-colored']}>Upgrade</span>
              {' '}
              now to become a TopHap
              Expert!
            </>
          )
        } else if (currentRole === 'basic') {
          title = (
            <>
              <span className={styles['th-colored']}>Upgrade</span>
              {' '}
              now to become a TopHap
              Pro!
            </>
          )
        }
      } else {
        title = (
          <>
            Start your
            {' '}
            <span className={styles['th-colored']}>free</span>
            {' '}
            7-day trial!
          </>
        )
      }
    } else {
      // when user doesn't log in.
      title = (
        <>
          Start your
          {' '}
          <span className={styles['th-colored']}>free</span>
          {' '}
          {trialDays}
          -day trial!
        </>
      )
    }

    return title
  }

  get pageDescription() {
    const { user } = this.props
    const currentRole = get(user.paymentInfo, 'role')

    let description

    if (currentRole && currentRole !== 'free') {
      if (currentRole === 'expert') {
        description = (
          <>
            If you need any support visit
            {' '}
            <a href={ACADEMY_PAGE} rel='noreferrer' target='_blank'>
              TopHap Academy,
            </a>
            {' '}
            read our
            {' '}
            <a href={FAQ_PAGE} rel='noreferrer' target='_blank'>
              FAQ
            </a>
            , or
            {' '}
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a onClick={this.showIntercom}>
              Chat with Us.
            </a>
          </>
        )
      } else {
        description =
          'Get instant access to Expert Analytics, Value Forecasting, CSV Export and more…'
      }
    } else {
      description = 'Pick a plan below.'
    }

    return description
  }

  get basicFeatures() {
    return (
      <div className={styles['th-features']}>
        <p>Includes:</p>
        <ul className='mb-4 list-disc'>
          <li>Property Search</li>
          <li>Rental Search</li>
          <li>Estimates</li>
          <li>Value Heatmaps</li>
        </ul>
      </div>
    )
  }

  get proFeatures() {
    return (
      <div className={styles['th-features']}>
        <p>Everything in Basic plus:</p>
        <ul className='mb-4 list-disc'>
          <li>Market Analytics</li>
          <li>Property Analytics</li>
          <li>Branded Portal</li>
          <li>
            Property Reports
            <Badge role='pro' text='new' />
          </li>
          <li>
            CMA Reports
            <Badge role='pro' text='new' />
          </li>
        </ul>
      </div>
    )
  }

  get proFeaturesWithoutBasic() {
    return (
      <div className={styles['th-features']}>
        <p>Includes:</p>
        <ul className='mb-4 list-disc'>
          <li>Market Analytics</li>
          <li>Property Analytics</li>
          <li>Branded Portal</li>
          <li>
            Property Reports
            <Badge role='pro' text='new' />
          </li>
          <li>
            CMA Reports
            <Badge role='pro' text='new' />
          </li>
        </ul>
      </div>
    )
  }

  get expertFeatures() {
    return (
      <div className={styles['th-features']}>
        <p>Everything in Pro plus:</p>
        <ul className='mb-4 list-disc'>
          <li>Expert Analytics</li>
          <li>Value Forecasting</li>
          <li>Custom Logo</li>
          <li>CMA+ Expert</li>
          <li>CSV Export</li>
        </ul>
      </div>
    )
  }

  get expertFeaturesWithoutComparison() {
    return (
      <div className={styles['th-features']}>
        <p>Everything plus:</p>
        <ul className='mb-4 list-disc'>
          <li>Expert Analytics</li>
          <li>Value Forecasting</li>
          <li>Custom Logo</li>
          <li>CMA+ Expert</li>
          <li>CSV Export</li>
        </ul>
      </div>
    )
  }

  render() {
    const { asyncStatus, user, width, selectPlan } = this.props
    const { isPlanHeaderSticky, period } = this.state
    const skipBasic = user.permissions?.partner?.startsWith('wfg')

    const currentPlanId = get(user.paymentInfo, 'plan')

    const planInfo = {
      currentRole: get(user.paymentInfo, 'role'),
      currentPlan: currentPlanId ? id2Plan(currentPlanId) : undefined,
      period
    }

    // const freeInfo = (
    //   <PlanInfo
    //     plan={plans[period].free}
    //     onSelect={this.onFree}
    //     {...planInfo}
    //   />
    // )
    const basicInfo = !skipBasic ? (
      <PlanInfo
        plan={plans[period].basic}
        onSelect={() => selectPlan(plans[period].basic)}
        {...planInfo}
      />
    ) : null
    const proInfo = (
      <PlanInfo
        plan={{
          ...plans[period].pro,
          ...(skipBasic ? { customPrice: 'WFG', recommend: false } : {})
        }}
        onSelect={() => selectPlan(plans[period].pro)}
        {...planInfo}
      />
    )
    const expertInfo = (
      <PlanInfo
        plan={{
          ...plans[period].expert,
          ...(skipBasic ? { recommend: true } : {})
        }}
        onSelect={() => selectPlan(plans[period].expert)}
        {...planInfo}
      />
    )
    const enterpriseInfo = (
      <PlanInfo
        plan={plans[period].enterprise}
        onSelect={this.onEnterprise}
        {...planInfo}
      />
    )

    const table = (
      <div className={styles['th-plan-section']}>
        <div
          ref={this.refPlanHeaders}
          className={cn(styles['th-plan-headers'], { [styles['th-stuck']]: isPlanHeaderSticky })}
        >
          <div className={cn(styles['th-plan-info'], styles['th-headers-description'])}>
            Pick your plan
            <svg
              className='svg-arrow'
              height='60'
              viewBox='0 0 76 40'
              width='114'
              xmlns='http://www.w3.org/2000/svg'
            >
              <title>Arrow</title>
              <path
                d='M65.283 35.714c-6.751-1.736-12.376-3.852-16.85-6.354-5.196 3.481-12.153 5.24-19.938 4.28C15.127 31.508 5.537 23.664.208 13.443a2.066 2.066 0 1 1 3.664-1.91c4.762 9.133 13.3 16.117 25.202 18.016 5.981.737 11.324-.354 15.5-2.65-5.97-4.37-9.006-9.727-9.006-16.107 0-13.157 21.149-13.844 22.309-.175.518 6.11-1.774 11.731-5.988 15.92 4.162 2.122 9.407 3.962 15.76 5.51l-1.21-1.498a2.066 2.066 0 0 1 3.217-2.594l5.32 6.595a2.066 2.066 0 0 1-1.112 3.303l-8.37 2.065a2.066 2.066 0 0 1-.99-4.012l.78-.192zM39.7 10.792c0 5.257 2.787 9.798 8.471 13.584 3.897-3.449 6.028-8.235 5.588-13.41-.716-8.445-14.059-8.01-14.059-.174z'
                fill='#333'
                fillRule='nonzero'
              />
            </svg>
          </div>
          {!skipBasic && basicInfo}
          {proInfo}
          {expertInfo}
          {enterpriseInfo}
        </div>

        <div className={styles['th-plan-descriptions']}>
          <div className={cn(styles['th-features'], styles['th-title-features'])} />
          {!skipBasic && this.basicFeatures}
          {skipBasic ? this.proFeaturesWithoutBasic : this.proFeatures}
          {skipBasic ? this.expertFeaturesWithoutComparison : this.expertFeatures}
          <div className={styles['th-features']} />
        </div>

        <div
          className={styles['th-recommended-features-block']}
          style={skipBasic ?
            { border: `1px solid ${plans.monthly.expert.color}` } :
            {}}
        />

        <div className={styles['th-plan-features']}>
          {features.map(e => (
            <div key={e.id}>
              <FeaturesHeader skipBasic={skipBasic} title={e.title} />
              {e.items.map(feature => (
                <Feature
                  key={feature.id}
                  role={feature.requireRole as TopHap.UserRole}
                  skipBasic={skipBasic}
                  title={feature.title}
                  tooltip={feature.tooltip}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    )

    const slider = (
      <Slider
        className={styles['th-plan-slider']}
        dots
        infinite={false}
        responsive={[
          {
            breakpoint: 720,
            settings: {
              initialSlide: 0,
              slidesToShow: 2,
              slidesToScroll: 2
            }
          },
          {
            breakpoint: 540,
            settings: {
              initialSlide: 1,
              slidesToShow: 1,
              slidesToScroll: 1,
              centerMode: true,
              centerPadding: '20px'
            }
          }
        ]}
        slidesToScroll={1}
        slidesToShow={3}
        speed={500}
      >
        {!user.permissions?.partner?.startsWith('wfg') && (
          <PlanCard
            color={plans[period].basic.color}
            infoComponent={(
              <>
                {basicInfo}
                {this.basicFeatures}
              </>
            )}
            role='basic'
          />
        )}
        <PlanCard
          color={plans[period].pro.color}
          infoComponent={(
            <>
              {proInfo}
              {this.proFeatures}
            </>
          )}
          role='pro'
        />
        <PlanCard
          color={plans[period].expert.color}
          infoComponent={(
            <>
              {expertInfo}
              {this.expertFeatures}
            </>
          )}
          role='expert'
        />
        <PlanCard
          color={plans[period].enterprise.color}
          infoComponent={enterpriseInfo}
          role='enterprise'
        />
      </Slider>
    )

    return (
      <Page className={styles['th-pricing']} title='Pricing and Plans | TopHap'>
        <Head>
          <meta
            key='description'
            content='Discover monthly and yearly pricing plans for access to TopHap. Try TopHap expert tier free for 14 days!'
            name='description'
          />
        </Head>
        <h1 className='text-center'>
          <span className='text-2xl font-extrabold tracking-tight text-gray-900 sm:text-4xl'>
            <span className='block'>TopHap Analytics Studio</span>
            {' '}
            <span className='block text-primary'>
              {this.pageTitle}
            </span>
          </span>
        </h1>
        {/* <h3 className='mb-10 text-3xl font-semibold tracking-wide leading-none text-center md:text-3xl xl:text-4xl font-heading'>
          TopHap Studio
        </h3>
        <h1 className={styles['th-page-title']}>{this.pageTitle}</h1> */}

        <div className={styles['th-page-description']}>{this.pageDescription}</div>

        <PeriodPicker
          onlyYearly={
            planInfo.currentPlan && planInfo.currentPlan.period === 'annual'
          }
          period={period}
          onChange={this.onPeriodChange}
        />

        {width > 1200 ? table : slider}

        <FAQ className={styles['th-faq']} items={user.permissions?.partner ? faq.slice(1, 4) : faq} />

        <OverlaySpinner
          visible={asyncStatus[GetCustomer.type] === 'request'}
        />
      </Page>
    )
  }
}

export default withRouter(Pricing)
